import config from '@config/index'
import { thumbtackCompleteV4, thumbtackStatusV4 } from '../service'

// Progress of a V4 Connect after the popup returns, so the Connect button can keep its spinner until the
// Integrations card switches to Disconnect. detail: { phase: 'started' | 'connected' | 'failed', status }
export const THUMBTACK_V4_PROGRESS_EVENT = 'thumbtack:v4:connect-progress'

export const emitThumbtackV4Progress = (phase, status) => {
  clearThumbtackV4StatusCache() // a connect changes the status the app-wide reconnect banner reads
  window.dispatchEvent(new CustomEvent(THUMBTACK_V4_PROGRESS_EVENT, { detail: { phase, status } }))
}

// --- Integrations card decisions, all from the server's /v4/status response -------------------------------

// The stored connection is V4 whatever its state (ready, pending_business, reconnect_required, disconnected): such
// accounts must always use the V4 endpoints, never the legacy ones, even when not currently connected. A V4
// disconnect keeps a "disconnected" marker, so a migrated merchant's leftover legacy settings never decide the card.
export const isV4Account = (status) => !!(status && status.apiVersion === 'v4')

export const isV4Connected = (status) => isV4Account(status) && !!status.connected

export const isV4ReconnectRequired = (status) => isV4Account(status) && !!status.reconnectRequired

// Connected, but no Thumbtack business bound yet (the Thumbtack user has several, or the lookup failed).
export const isV4BusinessSelectionRequired = (status) =>
  isV4Connected(status) && status.businessSelection !== 'selected'

// Name of the bound business as Thumbtack returned it, or '' when none is selected.
export const v4BusinessName = (status) =>
  isV4Connected(status) && status.businessSelection === 'selected' && status.businessName ? status.businessName : ''

// Error codes from the V4 endpoints, mapped to settings.* translation keys.
const THUMBTACK_V4_ERROR_KEYS = {
  business_already_connected: 'settings.thumbtack-business-already-connected',
  business_not_found: 'settings.thumbtack-business-not-found',
  reconnect_required: 'settings.thumbtack-connection-expired',
  consent_denied: 'settings.thumbtack-connection-cancelled'
}

export const thumbtackV4ErrorMessage = (t, code, fallbackKey) =>
  t(THUMBTACK_V4_ERROR_KEYS[code] || fallbackKey || 'settings.thumbtack-connect-failed')

// Error code from a rejected V4 call (catchHandler rejects with the response).
export const thumbtackV4ErrorCode = (res) => (res && res.data && res.data.error ? res.data.error : '')

// Codes integration-service returns when a Thumbtack reply from Messenger is not sent (RP-2730 Phase 2.2).
const THUMBTACK_REPLY_ERROR_CODES = [
  'thumbtack_conversation_not_found',
  'thumbtack_not_permitted',
  'thumbtack_rate_limited',
  'thumbtack_message_invalid',
  'thumbtack_business_mismatch',
  'thumbtack_business_required',
  'thumbtack_send_unconfirmed',
  'thumbtack_sent_not_saved',
  'thumbtack_unavailable',
  'reconnect_required',
  'not_connected'
]

// A failed Thumbtack reply with a known code becomes { message } in the user's language (Messenger's onError shows
// message first); anything else is returned unchanged so the existing error text still applies.
export const thumbtackReplyError = (t, res) => {
  const code = thumbtackV4ErrorCode(res)
  return THUMBTACK_REPLY_ERROR_CODES.indexOf(code) > -1
    ? { message: t(`messenger.thumbtack-reply-errors.${code}`) }
    : res
}

// Toast after a successful Connect, from the callback's status: names the business when one was bound.
export const thumbtackV4ConnectToast = (t, status) => {
  const name = v4BusinessName(status)
  if (name) return { type: 'success', message: t('settings.thumbtack-connected-to-business', { name }) }
  if (status && status.businessError)
    return { type: 'error', message: thumbtackV4ErrorMessage(t, status.businessError) }
  if (status && status.businessSelection === 'none_found') {
    return { type: 'error', message: t('settings.thumbtack-no-business-found') }
  }
  return { type: 'success', message: t('settings.thumbtack-connected-choose-business') }
}

// Show the Connect/Disconnect (OAuth) card instead of the legacy V1 email + Service ID form. The server's
// connectFlow says whether a new Connect would use V4 for this user, so a V4-enabled merchant with no
// connection and the old v2Thumbtack flag off still gets the Connect button.
export const shouldUseOAuthCard = ({ status, hasLegacyOAuthLink, v2ThumbtackFlag }) =>
  !!(hasLegacyOAuthLink || v2ThumbtackFlag || isV4Account(status) || (status && status.connectFlow === 'v4'))

// A V4 popup result always carries the state the server issued, plus a code or an error.
export const isThumbtackV4Message = (data) => !!(data && data.isThumbtack && data.state && (data.code || data.error))

// Accept popup messages only from this page's origin, a GoSite domain for this environment, or localhost.
export const isTrustedThumbtackOrigin = (origin) => {
  if (!origin) return false
  if (origin === window.location.origin) return true
  let hostname = ''
  try {
    hostname = new URL(origin).hostname
  } catch (e) {
    return false
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true
  const domain = String(config.domainName || '').replace(/^\./, '')
  if (!domain) return false
  return hostname === domain || hostname.endsWith(`.${domain}`)
}

// Sends the popup result to the server, which validates the state, exchanges the code and stores the
// tokens. Resolves to { ok: true, status } or { ok: false, error } - never throws, never sees tokens.
export const completeThumbtackV4 = async (data) => {
  try {
    const status = await thumbtackCompleteV4({ code: data.code, state: data.state, error: data.error })
    return { ok: true, status }
  } catch (res) {
    const error = res && res.data && res.data.error ? res.data.error : 'token_exchange_failed'
    return { ok: false, error }
  }
}

// --- App-wide reconnect warning (Phase 2.9c): Messenger and Contact Hub banners, thread and contact indicators ----

const STATUS_CACHE_MS = 5 * 60 * 1000
let statusCache = null // { userId, at, promise }

// /v4/status shared by the pages that warn about Thumbtack, so moving between them does not ask again. Kept for a
// few minutes per user (the selected location); force asks again (e.g. after a failed Thumbtack reply). A failed
// lookup resolves to null and is not kept: no warning is better than a false one.
export const getThumbtackV4StatusCached = ({ userId, force = false, now = Date.now } = {}) => {
  const key = userId ? String(userId) : ''
  if (!force && statusCache && statusCache.userId === key && now() - statusCache.at < STATUS_CACHE_MS) {
    return statusCache.promise
  }
  const entry = { userId: key, at: now() }
  entry.promise = thumbtackStatusV4().catch((e) => {
    console.warn('[thumbtack-v4 > getThumbtackV4StatusCached] status unavailable', e && e.status)
    if (statusCache === entry) statusCache = null
    return null
  })
  statusCache = entry
  return entry.promise
}

export const clearThumbtackV4StatusCache = () => {
  statusCache = null
}

// Whether to warn that Thumbtack must be reconnected. A V4 account (any state) is judged only by the server's
// reconnectRequired, never by leftover legacy settings; any other account by the legacy thumbtacksSettings.isError.
export const thumbtackNeedsReconnect = ({ status, accountSettings }) => {
  if (isV4Account(status)) return !!status.reconnectRequired
  const legacy = accountSettings && accountSettings.thumbtacksSettings
  return !!(legacy && legacy.isError)
}
